import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { useSession } from '@/features/auth/api/useSession'
import { givenFinanceWorkspace } from '@/features/cases/caseSections'
import { CaseDocuments } from '@/features/cases/components/CaseDocuments'
import { useCaseReceivables } from '@/features/finance/api/useReceivables'
import { CasePayments } from '@/features/finance/components/CasePayments'
import { ClientPaymentSheet } from '@/features/finance/components/ClientPaymentSheet'
import { CAP, can, canAny } from '@/lib/can'
import { formatMoneyString } from '@/lib/format'
import type { CaseDetail, Money, Receivable } from '@/types/domain'

import { BalanceCell, BalanceTiles } from './BalanceCells'
import { MoneyUnavailable } from './MoneyUnavailable'
import { TrancheStrip } from './TrancheStrip'

/* «Счета клиенту» (S-11 revision, direction A «Реестр»): what the client is
 * billed and what they have paid, top to bottom —
 *
 *   1. four tiles: the rate, «Выставлено», «Получено» and «Долг клиента»,
 *      every figure the server's (`clientTotals`, AD-9). A refund to the client
 *      (S-22f) is a note under «Получено» — «Возврат: …», below «не разнесено»
 *      when both are there — never a fifth tile: Locked visual decision 7
 *      fixes four. «Получено» arrives already net of it;
 *   2. the payment schedule (`TrancheStrip`) — only for a reader the
 *      workspace does not already show it to: the workspace's «Ставка по
 *      котировке» (`CaseRateSchedule`) is the same series with its invoices and
 *      its «Выставить счёт» act, and the owner read the two as one schedule
 *      printed twice (live test, 2026-10-17);
 *   3. «Счета и дебет-ноты» — Finance's workspace (`CaseDocuments`, with the
 *      rate schedule in it) — for a reader given it, with its primary act
 *      unchanged, under the `case-invoices` anchor `?section=documents` lands
 *      on;
 *   4. «Полученные платежи»: the case's `in` payments with their row acts.
 *
 * Above them, the section's one act (S-21): «Внести платёж», which opens «Платёж
 * клиента» — record the client's payment and say which documents it pays in
 * one step. It is offered to a reader who may record a client's payment
 * (`finance.payment.record`, or `finance.payment.client` — Sales), on a case
 * that takes money and names a client. It is the section's BLUE button while
 * an issued invoice or debit note is unpaid — the job is to collect — and
 * steps back to secondary otherwise for a reader who raises invoices, whose
 * schedule's «Выставить счёт» is then the blue one; a reader without the
 * invoice key has no «Выставить счёт», so «Внести платёж» is theirs. The rows
 * of «Счета и дебет-ноты» open the same sheet on their own document.
 *
 * Each block shows only to the readers who saw it in «Деньги»: the rail does
 * not offer this tab on a case whose money is forbidden, and the workspace
 * keeps its one rule (`givenFinanceWorkspace`). */

/** A block framed as a card of its own. On the block itself rather than a
 *  `Card` around it: both blocks render nothing while there is nothing (no
 *  series; a payments read in flight), and an empty card would be a bordered
 *  sliver. `Card`'s own surface, border and radius — and never a shadow. */
const FRAME =
  'bg-surface border-border-subtle overflow-hidden rounded-[var(--radius-DEFAULT)] border pt-4 pb-1'

/** A served amount without its sign — the advance tile states the client's
 *  credit as a positive figure under its own word. String surgery on the
 *  served decimal, never arithmetic (AD-9). */
function unsigned(money: Money): Money {
  return { ...money, amount: money.amount.replace(/^-/, '') }
}

/** Whether a served decimal is anything but zero — read off its digits, never
 *  parsed into a float (AD-9). */
function nonZero(amount: string): boolean {
  return /[1-9]/.test(amount)
}

/** Whether a served decimal is below zero. The server judges the sign at
 *  cents, so "-0.00" is not one it sends; it is read as zero all the same. */
function negative(amount: string): boolean {
  return /^-/.test(amount) && /[1-9]/.test(amount)
}

export function BillingSection({
  record,
  onRetry,
}: {
  record: CaseDetail
  /** Refetches the case, which is where the money block comes from. */
  onRetry?: () => void
}) {
  const { t } = useTranslation()
  const { data: user } = useSession()
  const forbidden = record.moneyUnavailable === 'forbidden'
  /* The client payment sheet's subject: open, and the row it was opened from
     (none from the header). */
  const [paying, setPaying] = useState<{ openedFrom?: Receivable } | null>(null)

  /* What the case's receivables say: whether it takes money and names a
     client, and whether an issued document is still unpaid. Reach-gated, so
     the same read answers Finance and Sales. */
  const receivables = useCaseReceivables(forbidden ? '' : record.id)

  /* A decision, not a failure: the rail does not offer this tab for such a
     case, and this is the guard for any other way in. */
  if (forbidden) return null

  const money = record.money
  const workspace = givenFinanceWorkspace(user, false)
  /* The same predicate `CaseDocuments` draws `CaseRateSchedule` on: the
     workspace, AND the invoice render key — a debit-note-only reader is given
     the workspace without the schedule, and keeps the strip. */
  const scheduleInWorkspace = workspace && can(user, CAP.documentInvoiceRender)

  /* «Внести платёж» — an affordance, never the gate: the server re-checks the
     key, the case's reach, its lifecycle and its client. */
  const served = receivables.data
  const mayPay =
    canAny(user, CAP.financePaymentRecord, CAP.financePaymentClient) &&
    served !== undefined &&
    served.acceptsMoney &&
    served.hasClient
  const unpaidDocument = (served?.items ?? []).some(
    (row) => row.kind !== 'tranche' && (row.status === 'open' || row.status === 'partial'),
  )
  /* One blue button: «Внести платёж» while a document is unpaid, or for a
     reader with no «Выставить счёт» of their own. */
  const payIsPrimary = unpaidDocument || !scheduleInWorkspace

  /* «Получено»'s notes, each on its own line: what is not yet allocated, per
     currency and never summed, then what was paid back to the client. */
  const receivedNotes = money
    ? [
        money.clientTotals.receivedUnallocated.length > 0
          ? t('cases.billing.receivedUnallocated', {
              amounts: money.clientTotals.receivedUnallocated
                .map((sum) => formatMoneyString(sum.amount, sum.currency))
                .join(' · '),
            })
          : undefined,
        nonZero(money.clientTotals.refunded.amount)
          ? t('cases.billing.refunded', {
              amount: formatMoneyString(
                money.clientTotals.refunded.amount,
                money.clientTotals.refunded.currency,
              ),
            })
          : undefined,
      ].filter((line) => line !== undefined)
    : []

  /* The four tiles. `debt` is SIGNED: below zero the client has paid ahead of
     the paper, and the tile says «Аванс клиента» with the figure unsigned
     rather than a debt of minus something. */
  const tiles = money ? (
    <BalanceTiles label={t('cases.billing.tilesLabel')}>
      <BalanceCell
        label={t('cases.billing.rate')}
        amount={money.hasRate === false ? undefined : money.rate}
        note={money.hasRate === false ? t('cases.billing.noRate') : undefined}
      />
      <BalanceCell
        label={t('cases.billing.invoiced')}
        amount={money.clientTotals.invoiced}
        note={t('cases.billing.invoicedNote')}
      />
      <BalanceCell
        label={t('cases.billing.received')}
        amount={money.clientTotals.received}
        note={
          receivedNotes.length > 0 ? (
            <>
              {receivedNotes.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </>
          ) : undefined
        }
      />
      {negative(money.clientTotals.debt.amount) ? (
        <BalanceCell
          label={t('cases.billing.advance')}
          amount={unsigned(money.clientTotals.debt)}
          note={t('cases.billing.advanceNote')}
        />
      ) : (
        <BalanceCell label={t('cases.billing.debt')} amount={money.clientTotals.debt} />
      )}
    </BalanceTiles>
  ) : (
    <MoneyUnavailable onRetry={onRetry} />
  )

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {mayPay ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-text-heading text-[15px] leading-tight font-normal tracking-[-0.2px]">
            {t('cases.tabs.billing')}
          </h2>
          <Button
            size="sm"
            variant={payIsPrimary ? 'primary' : 'secondary'}
            onClick={() => setPaying({})}
          >
            {t('finance.receivables.pay')}
          </Button>
        </div>
      ) : null}

      {tiles}

      {money && !scheduleInWorkspace ? (
        <TrancheStrip caseId={record.id} tranches={money.tranches} className={FRAME} />
      ) : null}

      {/* Finance's workspace, under the anchor the old `?section=documents`
          lands on: focusable for the reveal, never in the tab order, with no
          focus ring — the global `:focus-visible` paints a box-shadow. Its own
          card is titled «Счета и дебет-ноты», so the anchor adds no heading. */}
      {workspace ? (
        <div
          id="case-invoices"
          tabIndex={-1}
          aria-label={t('cases.documents.title')}
          className="outline-none focus-visible:shadow-none"
        >
          <CaseDocuments
            record={record}
            onPay={mayPay ? (row) => setPaying({ openedFrom: row }) : undefined}
            raiseVariant={mayPay && payIsPrimary ? 'secondary' : 'primary'}
          />
        </div>
      ) : null}

      <CasePayments caseId={record.id} direction="in" className={FRAME} />

      {/* Mounted only for a reader who may record one, like the button. */}
      {mayPay ? (
        <ClientPaymentSheet
          mode="record"
          open={paying !== null}
          onClose={() => setPaying(null)}
          record={record}
          openedFrom={paying?.openedFrom}
        />
      ) : null}
    </div>
  )
}
